/**
 * Shared shaping of SocialKit pipeline beats into chat steps — used by BOTH the live
 * ladder (streamed via Pusher) and the durable, collapsed "process" disclosure on a
 * finished tool result, so they always read identically.
 */

/** A live pipeline beat streamed from SocialKit (mirrors the server payload). */
export interface GenerationPhaseEvent {
  creativeId?: string
  // `broll` + `render` are the reel-only stages (the async video pipeline); the rest are
  // shared with the synchronous static/carousel pipelines.
  stage: 'decide' | 'imagery' | 'design' | 'judge' | 'revise' | 'broll' | 'render' | 'finalize'
  status: 'start' | 'done'
  angle?: string
  hook?: string
  photoSource?: 'real' | 'generated' | 'none'
  keptPhoto?: boolean
  score?: number
  verdict?: string
  singleFix?: string
  attempt?: number
}

/** One rendered step in the chat's process ladder / disclosure. */
export interface PhaseStep {
  id: string
  label: string
  detail?: string
  status: 'active' | 'done'
}

/** Turn a phase event into its step id, label, and (on done) its detail. */
export function describePhase(e: GenerationPhaseEvent): {
  id: string
  label: string
  detail?: string
} {
  switch (e.stage) {
    case 'decide':
      return {
        id: 'decide',
        label: e.status === 'done' ? 'Decided the angle' : 'Deciding the angle',
        // Prefer the chosen angle (generate); fall back to the revise rationale so the
        // engine's reasoning ("what I'm changing and why") is visible on the ladder.
        // `||` (not `??`) so an empty-string angle from a revise beat still falls back.
        detail: e.status === 'done' ? e.angle || e.singleFix : undefined,
      }
    case 'imagery': {
      const detail = e.keptPhoto
        ? 'Kept your photo'
        : e.photoSource === 'generated'
          ? 'Generated a new scene'
          : e.photoSource === 'real'
            ? 'Using your photo'
            : undefined
      return {
        id: 'imagery',
        label: e.keptPhoto ? 'Keeping your photo' : 'Sorting out the image',
        detail: e.status === 'done' ? detail : undefined,
      }
    }
    case 'design':
      return {
        id: 'design',
        label: e.status === 'done' ? 'Designed the layout' : 'Designing the layout',
      }
    case 'broll':
      return {
        id: 'broll',
        label: e.status === 'done' ? 'Filmed the scenes' : 'Filming the scenes',
      }
    case 'render':
      return {
        id: 'render',
        label: e.status === 'done' ? 'Rendered the reel' : 'Rendering the reel',
      }
    case 'judge':
      return {
        id: 'judge',
        label: e.status === 'done' ? 'Judged it' : 'Judging the post',
        detail:
          e.status === 'done' && typeof e.score === 'number' ? `Scored ${e.score}` : undefined,
      }
    case 'revise':
      // The `revise` stage is shared. The static/carousel quality loop carries an
      // `attempt` counter (>=1 on both start and done) and is judge-driven, so the
      // "weak spot" + score framing is right. The reel feedback-revise carries no
      // attempt/score (it re-renders from the user's note, no judge), so it reads
      // neutrally instead of implying a flaw was found.
      if ((e.attempt ?? 0) < 1) {
        return {
          id: 'revise',
          label: e.status === 'done' ? 'Reworked the reel' : 'Reworking the reel',
        }
      }
      // NEVER claim the weak spot was "fixed": this beat carries the engine's OWN pre-merge
      // verdict, but the authoritative final score is the judge step (which the server corrects
      // after merging the user's canvas edits — a merge can drop a "shipped" revise to blocked).
      // So the done label stays neutral ("Reworked the post · Now N") and the judge step owns the
      // verdict; the start label still names the weak spot the engine set out to fix.
      return {
        id: `revise-${e.attempt ?? 1}`,
        label: e.status === 'done' ? 'Reworked the post' : 'Fixing the weak spot',
        detail:
          e.status === 'done' && typeof e.score === 'number'
            ? `Now ${e.score}`
            : e.status === 'start' && e.singleFix
              ? e.singleFix
              : undefined,
      }
    default:
      return { id: 'finalize', label: e.status === 'done' ? 'Done' : 'Finishing up' }
  }
}

/**
 * Reduce a recorded list of phase events into ordered, de-duped steps (one per
 * stage, latest wins), all marked done. Used for the durable disclosure on a
 * finished result. `finalize` is dropped — it's just bookkeeping, not a real step.
 */
export function buildPhaseSteps(events: GenerationPhaseEvent[]): PhaseStep[] {
  const byId = new Map<string, PhaseStep>()
  for (const e of events) {
    if (e.stage === 'finalize') continue
    const { id, label, detail } = describePhase(e)
    const prev = byId.get(id)
    byId.set(id, { id, label, detail: detail ?? prev?.detail, status: 'done' })
  }
  return [...byId.values()]
}

/**
 * The single line a compact surface (Home / Posts generating card) shows for a live run:
 * the latest still-active step's label, or the last step once the run is wrapping up.
 * Returns undefined when there's nothing to show (no beats yet) so the caller can fall
 * back to its generic rotating copy.
 */
export function pickLivePhaseLabel(steps: PhaseStep[] | undefined): string | undefined {
  if (!steps || steps.length === 0) return undefined
  const active = [...steps].reverse().find(s => s.status === 'active')
  return (active ?? steps[steps.length - 1]).label
}
